import { useState } from 'react'
import * as Sentry from '@sentry/tanstackstart-react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, Search, Plus, Pencil, Trash2, Shield, User, Mail, Calendar, CheckCircle, XCircle, CreditCard, Gift } from 'lucide-react'
import { api, type AdminUser, type SubscriptionStatus } from '../lib/api'
import { queries } from '../lib/queries'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu'
import { Skeleton } from '../components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'

export const Route = createFileRoute('/admin/users')({
  head: () => ({
    meta: [{ title: 'Users | Admin | Unbreakable' }],
  }),
  component: AdminUsersPage,
})

function AdminUsersPage() {
  const queryClient = useQueryClient()
  const [searchEmail, setSearchEmail] = useState('')
  const [searchParams, setSearchParams] = useState<{ email?: string; after?: string; before?: string }>({})
  const [pageHistory, setPageHistory] = useState<string[]>([])
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '', name: '', role: 'USER' as 'ADMIN' | 'USER', subscriptionStatus: 'none' as SubscriptionStatus,
  })
  const [formError, setFormError] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: queries.admin.users(searchParams),
    queryFn: () => api.getAdminUsers({ limit: 25, ...searchParams }),
  })

  const users = data?.users ?? []
  const pagination = data?.pagination ?? { hasMore: false, before: null, after: null }

  const createMutation = useMutation({
    mutationFn: (data: { email: string; password: string; name?: string; role?: 'ADMIN' | 'USER' }) =>
      api.createAdminUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.admin.all })
      setShowCreateDialog(false)
      resetForm()
    },
    onError: (err) => { Sentry.captureException(err); setFormError(err instanceof Error ? err.message : 'Failed to create user') },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data, subscriptionStatus }: { id: string; data: Record<string, string>; subscriptionStatus?: SubscriptionStatus }) => {
      if (Object.keys(data).length > 0) {
        await api.updateAdminUser(id, data as { email?: string; name?: string; role?: 'ADMIN' | 'USER'; password?: string })
      }
      if (subscriptionStatus) {
        await api.updateUserSubscription(id, { subscriptionStatus })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.admin.all })
      setShowEditDialog(false)
      setSelectedUser(null)
      resetForm()
    },
    onError: (err) => { Sentry.captureException(err); setFormError(err instanceof Error ? err.message : 'Failed to update user') },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAdminUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.admin.all })
      setShowDeleteDialog(false)
      setSelectedUser(null)
    },
  })

  const resetForm = () => {
    setFormData({ email: '', password: '', confirmPassword: '', name: '', role: 'USER', subscriptionStatus: 'none' })
    setFormError(null)
  }

  const handleSearch = () => { setPageHistory([]); setSearchParams({ email: searchEmail || undefined }) }

  const handleCreate = () => {
    if (!formData.email || !formData.password) { setFormError('Email and password are required'); return }
    if (formData.password !== formData.confirmPassword) { setFormError('Passwords do not match'); return }
    createMutation.mutate({ email: formData.email, password: formData.password, name: formData.name || undefined, role: formData.role })
  }

  const handleUpdate = () => {
    if (!selectedUser) return
    const updateData: Record<string, string> = {}
    if (formData.email && formData.email !== selectedUser.email) updateData.email = formData.email
    if (formData.name !== undefined && formData.name !== selectedUser.name) updateData.name = formData.name
    if (formData.role && formData.role !== selectedUser.role) updateData.role = formData.role
    if (formData.password) {
      if (formData.password !== formData.confirmPassword) { setFormError('Passwords do not match'); return }
      updateData.password = formData.password
    }
    const subStatus = formData.subscriptionStatus !== selectedUser.subscriptionStatus ? formData.subscriptionStatus : undefined
    updateMutation.mutate({ id: selectedUser.id, data: updateData, subscriptionStatus: subStatus })
  }

  const openEditDialog = (user: AdminUser) => {
    setSelectedUser(user)
    setFormData({
      email: user.email, password: '', confirmPassword: '', name: user.name || '',
      role: user.role, subscriptionStatus: user.subscriptionStatus || 'none',
    })
    setFormError(null)
    setShowEditDialog(true)
  }

  const getSubscriptionBadge = (user: AdminUser) => {
    const status = user.subscriptionStatus || 'none'
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      active: 'default', comped: 'secondary', past_due: 'outline', canceled: 'destructive', none: 'outline',
    }
    return (
      <Badge variant={variants[status] || 'outline'} className="gap-1">
        {status === 'comped' ? <Gift size={12} /> : <CreditCard size={12} />}
        {status === 'comped' ? 'Comped' : status}
      </Badge>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-500">Manage all users in the system</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreateDialog(true) }}>
          <Plus size={16} className="mr-2" />
          Create User
        </Button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10"
            />
          </div>
          <Button variant="outline" onClick={handleSearch}>Search</Button>
          {searchEmail && (
            <Button variant="ghost" onClick={() => { setSearchEmail(''); setSearchParams({}) }}>Clear</Button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-700">{error instanceof Error ? error.message : 'Failed to load users'}</p>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Users</h2>
          <p className="text-sm text-gray-500">{users.length} user{users.length !== 1 ? 's' : ''} found</p>
        </div>

        {isLoading ? (
          <div className="p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No users found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Subscription</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand text-white text-xs font-medium">
                        {user.name ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) : user.email[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{user.name || 'No name'}</span>
                          {user.emailVerified ? <CheckCircle className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-amber-600" />}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Mail className="h-3 w-3" />
                          <span>{user.email}</span>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.role === 'ADMIN' ? 'default' : 'outline'} className="gap-1">
                      {user.role === 'ADMIN' ? <Shield className="h-3 w-3" /> : <User className="h-3 w-3" />}
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {getSubscriptionBadge(user)}
                      {user.subscriptionPlan && <span className="text-xs text-gray-500 block">{user.subscriptionPlan === 'monthly' ? 'Monthly' : 'Yearly'}</span>}
                      {user.subscriptionStatus === 'active' && user.currentPeriodEnd && <span className="text-xs text-gray-500 block">Renews {new Date(user.currentPeriodEnd).toLocaleDateString()}</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Calendar className="h-3 w-3" />
                      {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-gray-100">
                        <span className="sr-only">Actions</span>
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" /></svg>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(user)}>
                          <Pencil className="h-4 w-4 mr-2" />Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600" onClick={() => { setSelectedUser(user); setShowDeleteDialog(true) }}>
                          <Trash2 className="h-4 w-4 mr-2" />Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!isLoading && users.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t">
            <Button variant="outline" size="sm" onClick={() => {
              const prev = pageHistory[pageHistory.length - 1]
              setPageHistory((h) => h.slice(0, -1))
              setSearchParams(prev ? { after: prev, email: searchParams.email } : { email: searchParams.email })
            }} disabled={pageHistory.length === 0}>Previous</Button>
            <Button variant="outline" size="sm" onClick={() => {
              if (pagination.after) setPageHistory((h) => [...h, searchParams.after || '__first__'])
              setSearchParams({ after: pagination.after || undefined, email: searchParams.email })
            }} disabled={!pagination.hasMore}>Next</Button>
          </div>
        )}
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm() }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>Add a new user to the system</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {formError && <div className="rounded-lg bg-red-50 border border-red-200 p-3"><p className="text-sm text-red-700">{formError}</p></div>}
            <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="user@example.com" /></div>
            <div><Label>Name</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Doe" /></div>
            <div><Label>Password *</Label><Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Minimum 8 characters" /></div>
            <div><Label>Confirm Password *</Label><Input type="password" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} placeholder="Confirm password" /></div>
            <div>
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v as 'ADMIN' | 'USER' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USER">User</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleCreate} disabled={createMutation.isPending} className="flex-1">
                {createMutation.isPending ? 'Creating...' : 'Create'}
              </Button>
              <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm() }} className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) { setSelectedUser(null); resetForm() } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {formError && <div className="rounded-lg bg-red-50 border border-red-200 p-3"><p className="text-sm text-red-700">{formError}</p></div>}
            <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
            <div><Label>Name</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></div>
            <div><Label>New Password (leave blank to keep current)</Label><Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} /></div>
            {formData.password && <div><Label>Confirm New Password</Label><Input type="password" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} /></div>}
            <div>
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v as 'ADMIN' | 'USER' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USER">User</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subscription Status</Label>
              <Select value={formData.subscriptionStatus} onValueChange={(v) => setFormData({ ...formData, subscriptionStatus: v as SubscriptionStatus })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="active">Active (Paid)</SelectItem>
                  <SelectItem value="comped">Comped (Free Access)</SelectItem>
                  <SelectItem value="past_due">Past Due</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.role === 'ADMIN' ? 'Admin users always have full access regardless of subscription status.' : 'Set to "Comped" to grant free access without Stripe subscription.'}
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleUpdate} disabled={updateMutation.isPending} className="flex-1">
                {updateMutation.isPending ? 'Updating...' : 'Update'}
              </Button>
              <Button variant="outline" onClick={() => { setShowEditDialog(false); setSelectedUser(null); resetForm() }} className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.email}</strong>? This will permanently remove the user and all associated data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedUser(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => selectedUser && deleteMutation.mutate(selectedUser.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
